import React, { useState, useEffect, useRef } from 'react';
import { base44 } from '@/api/base44Client';
import { publicJobsOnly } from '@/lib/jobProvenance';
import { useAuth } from '@/lib/AuthContext';
import SavedSearches, { useSavedSearches } from '../components/SavedSearches';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import MobileSelect from "../components/MobileSelect";
import PullToRefresh from "../components/PullToRefresh";
import SEO from "../components/SEO";
import { 
  Search, 
  MapPin,
  BookmarkCheck, 
  Building2, 
  Clock, 
  DollarSign,
  Briefcase,
  ChevronRight,
  Heart,
  Stethoscope,
  Users,
  Shield,
  SlidersHorizontal,
  X,
  Bell,
  Plus,
  CheckCircle
} from 'lucide-react';
import { motion } from 'framer-motion';
import { jobDetailUrl } from '../components/jobSlug';
import SocialShareButtons from '../components/SocialShareButtons';
import JobAlertBar from '../components/JobAlertBar';

const SPECIALTIES = ["All", "Nursing", "Physician", "Physical Therapy", "Occupational Therapy", "Medical Assistant", "Pharmacy", "Radiology", "Laboratory", "Administration", "Other"];
const JOB_TYPES = ["All", "Full-time", "Part-time", "Contract", "Per Diem", "Travel"];

const US_STATES = [
  { value: "ALL", label: "All States" },
  { value: "AL", label: "Alabama" },
  { value: "AK", label: "Alaska" },
  { value: "AZ", label: "Arizona" },
  { value: "AR", label: "Arkansas" },
  { value: "CA", label: "California" },
  { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" },
  { value: "DE", label: "Delaware" },
  { value: "FL", label: "Florida" },
  { value: "GA", label: "Georgia" },
  { value: "HI", label: "Hawaii" },
  { value: "ID", label: "Idaho" },
  { value: "IL", label: "Illinois" },
  { value: "IN", label: "Indiana" },
  { value: "IA", label: "Iowa" },
  { value: "KS", label: "Kansas" },
  { value: "KY", label: "Kentucky" },
  { value: "LA", label: "Louisiana" },
  { value: "ME", label: "Maine" },
  { value: "MD", label: "Maryland" },
  { value: "MA", label: "Massachusetts" },
  { value: "MI", label: "Michigan" },
  { value: "MN", label: "Minnesota" },
  { value: "MS", label: "Mississippi" },
  { value: "MO", label: "Missouri" },
  { value: "MT", label: "Montana" },
  { value: "NE", label: "Nebraska" },
  { value: "NV", label: "Nevada" },
  { value: "NH", label: "New Hampshire" },
  { value: "NJ", label: "New Jersey" },
  { value: "NM", label: "New Mexico" },
  { value: "NY", label: "New York" },
  { value: "NC", label: "North Carolina" },
  { value: "ND", label: "North Dakota" },
  { value: "OH", label: "Ohio" },
  { value: "OK", label: "Oklahoma" },
  { value: "OR", label: "Oregon" },
  { value: "PA", label: "Pennsylvania" },
  { value: "RI", label: "Rhode Island" },
  { value: "SC", label: "South Carolina" },
  { value: "SD", label: "South Dakota" },
  { value: "TN", label: "Tennessee" },
  { value: "TX", label: "Texas" },
  { value: "UT", label: "Utah" },
  { value: "VT", label: "Vermont" },
  { value: "VA", label: "Virginia" },
  { value: "WA", label: "Washington" },
  { value: "WV", label: "West Virginia" },
  { value: "WI", label: "Wisconsin" },
  { value: "WY", label: "Wyoming" }
];

export default function Home() {
  const [searchTerm, setSearchTerm] = useState('');
  const [specialty, setSpecialty] = useState('All');
  const [jobType, setJobType] = useState('All');
  const [selectedState, setSelectedState] = useState('ALL');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [salaryMin, setSalaryMin] = useState('');
  const [salaryMax, setSalaryMax] = useState('');
  const [remoteOnly, setRemoteOnly] = useState(false);
  const [certifications, setCertifications] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [distanceRadius, setDistanceRadius] = useState('50');
  const [sortBy, setSortBy] = useState('date');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [showSaveAlertDialog, setShowSaveAlertDialog] = useState(false);
  const [alertName, setAlertName] = useState('');
  const { user } = useAuth();
  const searchRef = useRef(null);
  const queryClient = useQueryClient();
  const { savedSearches, saveSearch, removeSearch } = useSavedSearches();
  const [currentPage, setCurrentPage] = useState(1);
  const jobsPerPage = 20;

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const { data: jobs = [], isLoading } = useQuery({
    queryKey: ['jobs'],
    queryFn: async () => publicJobsOnly(await base44.entities.Job.filter({ status: 'Active' }, '-created_date', 2000)),
  });

  const handleRefresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['jobs'] });
  };

  const saveAlertMutation = useMutation({
    mutationFn: async (alertData) => {
      return await base44.entities.JobAlert.create(alertData);
    },
    onSuccess: () => {
      toast.success('Job alert saved successfully!');
      setShowSaveAlertDialog(false);
      setAlertName('');
    },
    onError: () => {
      toast.error('Failed to save job alert');
    }
  });

  const handleSaveAlert = () => {
    if (!user) {
      toast.error('Please sign in to save job alerts');
      base44.auth.redirectToLogin(window.location.href);
      return;
    }
    setShowSaveAlertDialog(true);
  };

  const confirmSaveAlert = () => {
    if (!alertName.trim()) {
      toast.error('Please enter a name for your alert');
      return;
    }

    const alertData = {
      user_id: user.id,
      user_email: user.email,
      name: alertName,
      profession: specialty !== 'All' ? specialty : undefined,
      specialty: specialty !== 'All' ? specialty : undefined,
      job_type: jobType !== 'All' ? jobType : undefined,
      location: locationFilter || undefined,
      salary_min: salaryMin ? Number(salaryMin) : undefined,
      salary_max: salaryMax ? Number(salaryMax) : undefined,
      distance_radius: distanceRadius !== 'any' ? Number(distanceRadius) : undefined,
      is_active: true
    };

    saveAlertMutation.mutate(alertData);
  };

  const filteredJobs = jobs.filter(job => {
    const matchesSearch = !searchTerm || 
      job.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.facility_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.specialty?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSpecialty = specialty === 'All' || job.specialty === specialty;
    const matchesType = jobType === 'All' || job.job_type === jobType;
    const matchesState = selectedState === 'ALL' || job.state?.toUpperCase() === selectedState;
    
    // Salary filter
    const matchesSalary = (!salaryMin || (job.salary_min && job.salary_min >= Number(salaryMin))) &&
                          (!salaryMax || (job.salary_max && job.salary_max <= Number(salaryMax)));
    
    // Remote filter
    const matchesRemote = !remoteOnly || job.remote_option === true;
    
    // Certifications filter
    const matchesCertifications = !certifications || 
      job.requirements?.toLowerCase().includes(certifications.toLowerCase()) ||
      job.description?.toLowerCase().includes(certifications.toLowerCase());
    
    // Location/Distance filter
    const matchesLocation = !locationFilter || 
      job.location?.toLowerCase().includes(locationFilter.toLowerCase()) ||
      job.city?.toLowerCase().includes(locationFilter.toLowerCase()) ||
      job.state?.toLowerCase().includes(locationFilter.toLowerCase());
    
    return matchesSearch && matchesSpecialty && matchesType && matchesState && matchesSalary && matchesRemote && matchesCertifications && matchesLocation;
  });

  // Sorting
  const sortedJobs = [...filteredJobs].sort((a, b) => {
    if (sortBy === 'date') {
      return new Date(b.created_date) - new Date(a.created_date);
    } else if (sortBy === 'salary_high') {
      return (b.salary_max || 0) - (a.salary_max || 0);
    } else if (sortBy === 'salary_low') {
      return (a.salary_min || 0) - (b.salary_min || 0);
    }
    return 0;
  });

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, specialty, jobType, selectedState, salaryMin, salaryMax, remoteOnly, certifications, locationFilter, sortBy]);

  // Pagination
  const totalPages = Math.ceil(sortedJobs.length / jobsPerPage);
  const startIndex = (currentPage - 1) * jobsPerPage;
  const endIndex = startIndex + jobsPerPage;
  const currentJobs = sortedJobs.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Generate suggestions based on search term and current filters (debounced)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm.trim().length > 1) {
        const availableJobs = jobs.filter(job => {
          const matchesSpecialty = specialty === 'All' || job.specialty === specialty;
          const matchesType = jobType === 'All' || job.job_type === jobType;
          const matchesState = selectedState === 'ALL' || job.state?.toUpperCase() === selectedState;
          return matchesSpecialty && matchesType && matchesState;
        });

        const titleMatches = availableJobs
          .filter(job => job.title?.toLowerCase().includes(searchTerm.toLowerCase()))
          .map(job => job.title)
          .filter((title, index, self) => self.indexOf(title) === index)
          .slice(0, 5);
        setSuggestions(titleMatches);
      } else {
        setSuggestions([]);
      }
    }, 150);

    return () => clearTimeout(timer);
  }, [searchTerm, jobs, specialty, jobType, selectedState]);

  const formatSalary = (job) => {
    if (!job.salary_min && !job.salary_max) return null;
    const format = (num) => {
      if (job.salary_type === 'Hourly') {
        return `$${num.toLocaleString()}/hr`;
      } else if (job.salary_type === 'Annual' && num >= 50000) {
        // Only use 'k' format for annual salaries >= 50k
        return `$${(num/1000).toFixed(0)}k`;
      } else {
        // Show exact number for weekly salaries and smaller annual salaries
        return `$${num.toLocaleString()}`;
      }
    };
    if (job.salary_min && job.salary_max) return `${format(job.salary_min)} - ${format(job.salary_max)}`;
    return job.salary_min ? `From ${format(job.salary_min)}` : `Up to ${format(job.salary_max)}`;
  };

  const homeSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Clinivo",
    "description": "Healthcare job board connecting professionals with top medical facilities",
    "url": "https://clinivo.net",
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": "https://clinivo.net/?search={search_term_string}"
      },
      "query-input": "required name=search_term_string"
    }
  };

  return (
    <PullToRefresh onRefresh={handleRefresh}>
      <SEO 
        title="Healthcare Jobs & Medical Staffing | Clinivo"
        description={`Find ${jobs.length > 0 ? `${jobs.length}+ ` : ''}RN, LPN, travel nursing, and healthcare jobs, or post your openings to reach qualified medical professionals. The healthcare job board for job seekers and employers.`}
        keywords="healthcare jobs, nursing jobs, RN jobs, LPN jobs, travel nursing jobs, healthcare staffing, hire nurses, post healthcare jobs, medical careers, hospital jobs, healthcare job board"
        url="https://clinivo.net/"
        schema={homeSchema}
      />
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white dark:from-slate-950 dark:to-slate-900">
      <JobAlertBar />
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-teal-600 via-teal-700 to-cyan-800">
        <img 
          src="https://images.unsplash.com/photo-1579684385127-1ef15d508118?auto=format&fit=crop&w=1280&q=70"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover opacity-10"
          loading="eager"
          fetchPriority="high"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-teal-900/50 to-transparent" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight">
              Healthcare Jobs That Match Your Career
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-teal-100/90 max-w-2xl">
              Search RN, LPN, travel nursing, therapy and allied health jobs from verified healthcare employers nationwide.
            </p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <a href="#job-search" className="inline-flex items-center gap-2 bg-white text-teal-700 hover:bg-teal-50 font-semibold text-base px-6 py-3 rounded-xl transition-colors shadow-lg">
                <Search className="h-5 w-5" />
                Search Jobs
              </a>
              <button onClick={handleSaveAlert} className="inline-flex items-center gap-2 bg-teal-500/30 hover:bg-teal-500/40 text-white border border-white/30 font-semibold text-base px-6 py-3 rounded-xl transition-colors">
                <Bell className="h-5 w-5" />
                Create a Free Job Alert
              </button>
            </div>
            <div className="mt-4 flex flex-wrap gap-4 text-teal-100/80 text-sm">
              <span className="flex items-center gap-1.5"><CheckCircle className="h-4 w-4" /> No account required to browse</span>
              <span className="flex items-center gap-1.5"><CheckCircle className="h-4 w-4" /> Free for job seekers</span>
            </div>
            {/* Nursing quick links */}
            <div className="mt-5 flex flex-wrap gap-2">
              <Link to={createPageUrl('NursingJobs')}>
                <span className="inline-flex items-center gap-1.5 bg-white/15 hover:bg-white/25 text-white text-sm font-medium px-3 py-1.5 rounded-full transition-colors border border-white/20">
                  <Stethoscope className="h-3.5 w-3.5" /> RN Jobs
                </span>
              </Link>
              <Link to={createPageUrl('NursingJobs?type=lpn')}>
                <span className="inline-flex items-center gap-1.5 bg-white/15 hover:bg-white/25 text-white text-sm font-medium px-3 py-1.5 rounded-full transition-colors border border-white/20">
                  <Heart className="h-3.5 w-3.5" /> LPN / LVN Jobs
                </span>
              </Link>
              <Link to={createPageUrl('JobsByLocation?path=travel-nursing-jobs')}>
                <span className="inline-flex items-center gap-1.5 bg-white/15 hover:bg-white/25 text-white text-sm font-medium px-3 py-1.5 rounded-full transition-colors border border-white/20">
                  <Briefcase className="h-3.5 w-3.5" /> Travel Nursing
                </span>
              </Link>
              <Link to={createPageUrl('JobSpecialties')}>
                <span className="inline-flex items-center gap-1.5 bg-white/15 hover:bg-white/25 text-white text-sm font-medium px-3 py-1.5 rounded-full transition-colors border border-white/20">
                  <Stethoscope className="h-3.5 w-3.5" /> All Specialties
                </span>
              </Link>
            </div>
          </motion.div>

          {/* Search Card */}
          <motion.div 
            id="job-search"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-10 bg-white rounded-2xl shadow-2xl p-4 sm:p-6 max-w-4xl scroll-mt-20"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="relative" ref={searchRef}>
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400 z-10" />
                <Input 
                  placeholder="Job title or keyword"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setShowSuggestions(true);
                  }}
                  onFocus={() => setShowSuggestions(true)}
                  className="pl-10 h-12 border-slate-200 focus:border-teal-500 focus:ring-teal-500"
                />
                {showSuggestions && suggestions.length > 0 && (
                  <div className="absolute top-full mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
                    {suggestions.map((suggestion, idx) => (
                      <button
                        key={idx}
                        onClick={() => {
                          setSearchTerm(suggestion);
                          setShowSuggestions(false);
                        }}
                        className="w-full text-left px-4 py-2 hover:bg-slate-50 transition-colors text-sm"
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <Select value={selectedState} onValueChange={setSelectedState}>
                <SelectTrigger className="h-12 border-slate-200" aria-label="Filter jobs by state">
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-slate-400" aria-hidden="true" />
                    <SelectValue placeholder="All States" />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  {US_STATES.map(state => (
                    <SelectItem key={state.value} value={state.value}>
                      {state.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div>
                <Label className="text-sm font-medium mb-1.5 block text-slate-700 dark:text-slate-300">Profession</Label>
                <MobileSelect
                  value={specialty}
                  onValueChange={setSpecialty}
                  options={SPECIALTIES.map(s => ({ value: s, label: s }))}
                  placeholder="Specialty"
                  label="Select Specialty"
                />
              </div>
              <div>
                <Label className="text-sm font-medium mb-1.5 block text-slate-700 dark:text-slate-300">Job Type</Label>
                <MobileSelect
                  value={jobType}
                  onValueChange={setJobType}
                  options={JOB_TYPES.map(t => ({ value: t, label: t }))}
                  placeholder="Job Type"
                  label="Select Job Type"
                />
              </div>
            </div>

            {/* Advanced Filters */}
            <div className="mt-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                className="gap-2 text-slate-600"
              >
                <SlidersHorizontal className="h-4 w-4" />
                {showAdvancedFilters ? 'Hide' : 'Show'} Advanced Filters
              </Button>

              {showAdvancedFilters && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mt-4 p-4 border-t grid grid-cols-1 md:grid-cols-2 gap-4"
                >
                  <div>
                    <Label className="text-sm font-medium mb-2 block">Salary Range</Label>
                    <div className="flex gap-2">
                      <Input
                        type="number"
                        placeholder="Min"
                        value={salaryMin}
                        onChange={(e) => setSalaryMin(e.target.value)}
                        className="h-10"
                      />
                      <Input
                        type="number"
                        placeholder="Max"
                        value={salaryMax}
                        onChange={(e) => setSalaryMax(e.target.value)}
                        className="h-10"
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="text-sm font-medium mb-2 block">Certifications</Label>
                    <Input
                      placeholder="e.g., RN, BSN, ACLS"
                      value={certifications}
                      onChange={(e) => setCertifications(e.target.value)}
                      className="h-10"
                    />
                  </div>

                  <div>
                    <Label className="text-sm font-medium mb-2 block">Location Filter</Label>
                    <Input
                      placeholder="City, State or ZIP"
                      value={locationFilter}
                      onChange={(e) => setLocationFilter(e.target.value)}
                      className="h-10"
                    />
                  </div>

                  <div>
                    <Label className="text-sm font-medium mb-2 block">Distance Radius</Label>
                    <Select value={distanceRadius} onValueChange={setDistanceRadius}>
                      <SelectTrigger className="h-10">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">Within 10 miles</SelectItem>
                        <SelectItem value="25">Within 25 miles</SelectItem>
                        <SelectItem value="50">Within 50 miles</SelectItem>
                        <SelectItem value="100">Within 100 miles</SelectItem>
                        <SelectItem value="any">Any distance</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-medium">Remote Only</Label>
                    <Switch checked={remoteOnly} onCheckedChange={setRemoteOnly} />
                  </div>

                  {(salaryMin || salaryMax || remoteOnly || certifications || locationFilter) && (
                    <div className="md:col-span-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSalaryMin('');
                          setSalaryMax('');
                          setRemoteOnly(false);
                          setCertifications('');
                          setLocationFilter('');
                          setDistanceRadius('50');
                        }}
                        className="gap-2 text-red-600 hover:text-red-700"
                      >
                        <X className="h-4 w-4" />
                        Clear All Filters
                      </Button>
                    </div>
                  )}
                </motion.div>
              )}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 relative z-10">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { icon: Briefcase, label: 'Open Positions', value: jobs.length },
            { icon: Building2, label: 'Active Employers', value: new Set(jobs.map(j => j.facility_id).filter(Boolean)).size },
            { icon: Stethoscope, label: 'Specialties Hiring', value: new Set(jobs.map(j => j.specialty).filter(Boolean)).size },
            { icon: MapPin, label: 'States Covered', value: new Set(jobs.map(j => j.state).filter(Boolean)).size },
          ].map((stat, i) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.3 + i * 0.1 }}
            >
              <Card className="p-4 sm:p-6 bg-white border-0 shadow-lg hover:shadow-xl transition-shadow">
                 <stat.icon className="h-8 w-8 text-teal-600 mb-3" aria-hidden="true" />
                 <p className="text-2xl sm:text-3xl font-bold text-slate-900">{isLoading || jobs.length === 0 ? '—' : stat.value}</p>
                 <p className="text-sm text-slate-500 mt-1">{stat.label}</p>
               </Card>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Popular Specialties & Locations */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid sm:grid-cols-2 gap-8">
          <div>
            <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">Popular Specialties</h2>
            <div className="flex flex-wrap gap-2">
              {['Nursing', 'Physician', 'Physical Therapy', 'Pharmacy', 'Radiology', 'Occupational Therapy'].map(spec => (
                <Link key={spec} to={`/jobs/${spec.toLowerCase().replace(/\s+/g, '-')}`}>
                  <span className="inline-flex items-center text-sm font-medium text-teal-700 dark:text-teal-400 bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 hover:border-teal-400 hover:bg-teal-100 dark:hover:bg-teal-900/30 px-3 py-1.5 rounded-full transition-colors cursor-pointer">
                    {spec}
                  </span>
                </Link>
              ))}
            </div>
          </div>
          <div>
            <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">Popular Locations</h2>
            <div className="flex flex-wrap gap-2">
              {[
                { label: 'California', slug: 'california' },
                { label: 'Texas', slug: 'texas' },
                { label: 'Florida', slug: 'florida' },
                { label: 'New York', slug: 'new-york' },
                { label: 'Illinois', slug: 'illinois' },
                { label: 'Pennsylvania', slug: 'pennsylvania' },
              ].map(state => (
                <Link key={state.label} to={`/jobs/nursing/${state.slug}`}>
                  <span className="inline-flex items-center text-sm font-medium text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:border-teal-400 hover:bg-teal-50 dark:hover:bg-slate-700 px-3 py-1.5 rounded-full transition-colors cursor-pointer">
                    {state.label}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Job Listings */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-8 gap-4">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">Latest Opportunities</h2>
            <p className="text-slate-500 mt-1">
              {isLoading || sortedJobs.length === 0
                ? 'Browse current healthcare opportunities'
                : `${sortedJobs.length} positions available`}
            </p>
            <div className="mt-2">
              <SavedSearches
                savedSearches={savedSearches}
                onApply={(filters) => {
                  if (filters.searchTerm !== undefined) setSearchTerm(filters.searchTerm);
                  if (filters.specialty !== undefined) setSpecialty(filters.specialty);
                  if (filters.jobType !== undefined) setJobType(filters.jobType);
                  if (filters.locationFilter !== undefined) setLocationFilter(filters.locationFilter);
                  if (filters.selectedState !== undefined) setSelectedState(filters.selectedState);
                }}
                onRemove={removeSearch}
              />
            </div>
          </div>
          <div className="flex gap-3">
            {(specialty !== 'All' || jobType !== 'All' || selectedState !== 'ALL' || locationFilter || certifications || salaryMin || salaryMax || remoteOnly) && (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => saveSearch({ searchTerm, specialty, jobType, locationFilter, selectedState })}
                  className="gap-2"
                >
                  <BookmarkCheck className="h-4 w-4" />
                  Save Search
                </Button>
                <Button
                  variant="outline"
                  onClick={handleSaveAlert}
                  className="gap-2"
                >
                  <Bell className="h-4 w-4" />
                  Save as Alert
                </Button>
              </div>
            )}
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-48" aria-label="Sort job listings">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="date">Most Recent</SelectItem>
                <SelectItem value="salary_high">Salary: High to Low</SelectItem>
                <SelectItem value="salary_low">Salary: Low to High</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {isLoading ? (
          <div className="grid gap-4">
            {[1,2,3].map(i => (
              <div key={i} className="h-40 bg-slate-100 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : sortedJobs.length === 0 ? (
          <div className="text-center py-20">
            <Stethoscope className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-700">No jobs found</h3>
            <p className="text-slate-500 mt-2">Try adjusting your search filters</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {currentJobs.map((job, index) => (
              <motion.div
                key={job.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
              >
                <Link to={createPageUrl(jobDetailUrl(job))}>
                  <Card className="p-6 hover:shadow-lg transition-all duration-300 border-0 shadow-md group cursor-pointer bg-white">
                    <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                      <div className="flex-1">
                        <div className="flex items-start gap-4">
                          <div className="h-14 w-14 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center flex-shrink-0" role="img" aria-label="Healthcare facility">
                            <Building2 className="h-7 w-7 text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="text-lg font-semibold text-slate-900 group-hover:text-teal-600 transition-colors">
                              {job.title}
                            </h3>
                            <p className="text-slate-600 font-medium">{job.facility_name}</p>
                            <div className="flex flex-wrap items-center gap-3 mt-3 text-sm text-slate-500">
                              <span className="flex items-center gap-1">
                                <MapPin className="h-4 w-4" />
                                {job.city && job.state ? `${job.city}, ${job.state}` : job.location}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="h-4 w-4" />
                                {job.job_type}
                              </span>
                              {formatSalary(job) && (
                                <span className="flex items-center gap-1">
                                  <DollarSign className="h-4 w-4" />
                                  {formatSalary(job)}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 lg:flex-col lg:items-end">
                        <div className="flex flex-wrap gap-2 lg:items-end">
                          <Badge variant="secondary" className="bg-teal-50 text-teal-700 hover:bg-teal-100">
                            {job.specialty}
                          </Badge>
                          {job.referral_bonus_amount && (
                            <Badge className="bg-gradient-to-r from-purple-500 to-pink-500 text-white border-0">
                              💰 Referral Bonus
                            </Badge>
                          )}
                          {job.sign_on_bonus_amount && (
                            <Badge className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white border-0">
                              🎁 Sign-On Bonus
                            </Badge>
                          )}
                          {job.completion_bonus_amount && (
                            <Badge className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white border-0">
                              ⭐ Completion Bonus
                            </Badge>
                          )}
                        </div>
                        <ChevronRight className="h-5 w-5 text-slate-400 group-hover:text-teal-600 group-hover:translate-x-1 transition-all hidden lg:block" />
                      </div>
                    </div>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-8">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Previous
            </Button>
            <div className="flex gap-1">
              {[...Array(totalPages)].map((_, i) => {
                const page = i + 1;
                if (
                  page === 1 ||
                  page === totalPages ||
                  (page >= currentPage - 1 && page <= currentPage + 1)
                ) {
                  return (
                    <Button
                      key={page}
                      variant={currentPage === page ? "default" : "outline"}
                      size="sm"
                      onClick={() => handlePageChange(page)}
                      className={currentPage === page ? "bg-teal-600 hover:bg-teal-700" : ""}
                    >
                      {page}
                    </Button>
                  );
                } else if (page === currentPage - 2 || page === currentPage + 2) {
                  return <span key={page} className="px-2 text-slate-400">...</span>;
                }
                return null;
              })}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next
            </Button>
          </div>
        )}
      </div>

      {/* Newsletter Banner */}
      <div className="bg-gradient-to-r from-teal-600 to-cyan-600 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">Get the Top Jobs Every Week</h2>
          <p className="text-teal-100 text-lg mb-6 max-w-xl mx-auto">Subscribe to our free weekly newsletter — curated healthcare jobs by specialty, straight to your inbox.</p>
          <Link to={createPageUrl('Newsletter')}>
            <Button size="lg" className="bg-white text-teal-600 hover:bg-teal-50 px-8 h-12 text-base font-semibold gap-2">
              <Bell className="h-5 w-5" />
              Subscribe for Free
            </Button>
          </Link>
        </div>
      </div>

      {/* Community Resources */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h2 className="text-2xl font-bold text-slate-900 mb-8 text-center">More Ways to Advance Your Career</h2>
        {/* Nursing specialty quick links */}
        <div className="mb-8 bg-teal-50 rounded-2xl p-6">
          <p className="text-sm font-semibold text-teal-700 uppercase tracking-wide mb-4">Browse by Nursing Role</p>
          <div className="flex flex-wrap gap-2">
            {[
              { label: 'RN Jobs', path: 'NursingJobs' },
              { label: 'LPN / LVN Jobs', path: 'NursingJobs?type=lpn' },
              { label: 'Travel RN Jobs', path: 'JobsByLocation?path=travel-nursing-jobs' },
              { label: 'ICU / Critical Care', path: 'Home?specialty=Nursing' },
              { label: 'Per Diem Nursing', path: 'Home?specialty=Nursing' },
              { label: 'Nursing Salary Guide', path: 'SalaryGuide' },
            ].map(item => (
              <Link key={item.label} to={createPageUrl(item.path)}>
                <span className="inline-flex items-center gap-1 text-sm font-medium text-teal-700 bg-white border border-teal-200 hover:border-teal-400 hover:bg-teal-100 px-3 py-1.5 rounded-full transition-colors cursor-pointer">
                  {item.label}
                </span>
              </Link>
            ))}
          </div>
        </div>
        <div className="grid sm:grid-cols-3 gap-6">
          <Link to={createPageUrl('ResumeBuilder')}>
            <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer group border-0 shadow-md bg-white">
              <div className="h-12 w-12 rounded-xl bg-teal-100 flex items-center justify-center mb-4">
                <Plus className="h-6 w-6 text-teal-600" />
              </div>
              <h3 className="font-semibold text-slate-900 group-hover:text-teal-600 transition-colors mb-2">Resume Builder</h3>
              <p className="text-slate-500 text-sm">Build a professional resume with AI assistance and download as PDF.</p>
            </Card>
          </Link>
          <Link to={createPageUrl('Forum')}>
            <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer group border-0 shadow-md bg-white">
              <div className="h-12 w-12 rounded-xl bg-cyan-100 flex items-center justify-center mb-4">
                <Users className="h-6 w-6 text-cyan-600" />
              </div>
              <h3 className="font-semibold text-slate-900 group-hover:text-teal-600 transition-colors mb-2">Community Forum</h3>
              <p className="text-slate-500 text-sm">Ask questions, share advice, and connect with peers across every specialty.</p>
            </Card>
          </Link>
          <Link to={createPageUrl('SalaryGuide')}>
            <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer group border-0 shadow-md bg-white">
              <div className="h-12 w-12 rounded-xl bg-emerald-100 flex items-center justify-center mb-4">
                <Heart className="h-6 w-6 text-emerald-600" />
              </div>
              <h3 className="font-semibold text-slate-900 group-hover:text-teal-600 transition-colors mb-2">Salary Guide</h3>
              <p className="text-slate-500 text-sm">Compare salaries by specialty and location so you can negotiate with confidence.</p>
            </Card>
          </Link>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Healthcare Facility or Staffing Agency?
          </h2>
          <p className="text-slate-300 text-lg max-w-2xl mx-auto mb-8">
            Post your first job free and reach thousands of qualified healthcare professionals today. No credit card required.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to={createPageUrl('PostJob')}>
              <Button size="lg" className="bg-teal-500 hover:bg-teal-600 text-white px-8 h-12 text-lg gap-2">
                Post a Job Free
                <ChevronRight className="h-5 w-5" />
              </Button>
            </Link>
            <Link to={createPageUrl('ForEmployers')}>
              <Button size="lg" variant="outline" className="border-white/40 bg-transparent text-white hover:bg-white/10 hover:text-white px-8 h-12 text-lg gap-2">
                <Building2 className="h-5 w-5" />
                Explore Employer Solutions
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Tools Section */}
      <div className="bg-white dark:bg-slate-900 py-12 border-t dark:border-slate-800">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white text-center mb-8">Free Career Tools</h2>
          <div className="grid sm:grid-cols-2 gap-6">
            <Link to={createPageUrl('SalaryCalculator')}>
              <Card className="p-6 border-0 shadow-md hover:shadow-lg transition-shadow cursor-pointer h-full">
                <div className="flex items-start gap-4">
                  <div className="h-12 w-12 rounded-lg bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center flex-shrink-0">
                    <DollarSign className="h-6 w-6 text-teal-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 dark:text-white mb-1">Salary Calculator</h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400">Estimate your healthcare salary by specialty, location, and experience level.</p>
                  </div>
                </div>
              </Card>
            </Link>
            <Link to={createPageUrl('JobSpecialties')}>
              <Card className="p-6 border-0 shadow-md hover:shadow-lg transition-shadow cursor-pointer h-full">
                <div className="flex items-start gap-4">
                  <div className="h-12 w-12 rounded-lg bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center flex-shrink-0">
                    <Stethoscope className="h-6 w-6 text-teal-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 dark:text-white mb-1">Browse by Specialty</h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400">Explore healthcare career paths and find jobs by your medical specialty.</p>
                  </div>
                </div>
              </Card>
            </Link>
          </div>
        </div>
      </div>

      {/* Share Section */}
      <div className="bg-teal-50 dark:bg-slate-900 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
            Know Someone Looking for a Healthcare Job?
          </h2>
          <p className="text-slate-600 dark:text-slate-300 mb-6">
            Share Clinivo with your network and help colleagues find their next opportunity.
          </p>
          <SocialShareButtons
            url="https://clinivo.net"
            title="Clinivo - Healthcare Jobs & Nursing Careers"
            description="Find RN, LPN, travel nursing, and healthcare jobs nationwide on Clinivo."
          />
        </div>
      </div>

      {/* Save Alert Dialog */}
      <Dialog open={showSaveAlertDialog} onOpenChange={setShowSaveAlertDialog}>
        <DialogContent className="dark:bg-slate-900 dark:border-slate-800 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="dark:text-white">Save Job Alert</DialogTitle>
            <DialogDescription className="dark:text-slate-400">
              Get notified when new jobs matching your criteria are posted.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="dark:text-slate-200">Alert Name *</Label>
              <Input
                value={alertName}
                onChange={(e) => setAlertName(e.target.value)}
                placeholder="e.g., RN positions in California"
                className="mt-2 dark:bg-slate-800 dark:border-slate-700 dark:text-white"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="dark:text-slate-200">Specialty</Label>
                <Select value={specialty} onValueChange={setSpecialty}>
                  <SelectTrigger className="mt-2 dark:bg-slate-800 dark:border-slate-700 dark:text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SPECIALTIES.map(s => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="dark:text-slate-200">Job Type</Label>
                <Select value={jobType} onValueChange={setJobType}>
                  <SelectTrigger className="mt-2 dark:bg-slate-800 dark:border-slate-700 dark:text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {JOB_TYPES.map(t => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="dark:text-slate-200">City/Location</Label>
              <Input
                value={locationFilter}
                onChange={(e) => setLocationFilter(e.target.value)}
                placeholder="e.g., Los Angeles, CA"
                className="mt-2 dark:bg-slate-800 dark:border-slate-700 dark:text-white"
              />
            </div>

            <div>
              <Label className="dark:text-slate-200">Distance Radius</Label>
              <Select value={distanceRadius} onValueChange={setDistanceRadius}>
                <SelectTrigger className="mt-2 dark:bg-slate-800 dark:border-slate-700 dark:text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">Within 10 miles</SelectItem>
                  <SelectItem value="25">Within 25 miles</SelectItem>
                  <SelectItem value="50">Within 50 miles</SelectItem>
                  <SelectItem value="100">Within 100 miles</SelectItem>
                  <SelectItem value="any">Any distance</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="dark:text-slate-200">Salary Range (Optional)</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <Input
                  type="number"
                  placeholder="Min"
                  value={salaryMin}
                  onChange={(e) => setSalaryMin(e.target.value)}
                  className="dark:bg-slate-800 dark:border-slate-700 dark:text-white"
                />
                <Input
                  type="number"
                  placeholder="Max"
                  value={salaryMax}
                  onChange={(e) => setSalaryMax(e.target.value)}
                  className="dark:bg-slate-800 dark:border-slate-700 dark:text-white"
                />
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowSaveAlertDialog(false)}
                className="flex-1 select-none dark:bg-slate-800 dark:text-white dark:hover:bg-slate-700"
              >
                Cancel
              </Button>
              <Button
                onClick={confirmSaveAlert}
                disabled={saveAlertMutation.isPending}
                className="flex-1 select-none"
              >
                {saveAlertMutation.isPending ? 'Saving...' : 'Save Alert'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
    </PullToRefresh>
  );
}